import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Wifi, UtensilsCrossed, Users, Heart, Coffee, Dumbbell, Waves, Car, MapPin, Mars, Venus, VenusAndMars, BadgeCheck } from "lucide-react";
import { Badge } from "../ui/badge"; // Assuming ui components are in ../ui
import { Button } from "../ui/button"; // Assuming ui components are in ../ui
import { cn } from "../../lib/utils"; // Assuming cn utility is here
import { getImageUrl } from "../../utils/imageUrl"; // Assuming getImageUrl utility is here

const DEFAULT_PROPERTY_IMAGE = "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&auto=format&fit=crop&q=60";

const amenityIcons: Record<string, React.ElementType> = {
  wifi: Wifi,
  meals: UtensilsCrossed,
  pool: Waves,
  cafe: Coffee,
  gym: Dumbbell,
  parking: Car,
};

export const genderTypeConfig: Record<string, { icon: React.ElementType; label: string; className: string }> = {
  male: { icon: Mars, label: "Male", className: "bg-blue-500/90 text-white" },
  female: { icon: Venus, label: "Female", className: "bg-pink-500/90 text-white" },
  unisex: { icon: VenusAndMars, label: "Unisex", className: "bg-purple-500/90 text-white" },
};

const amenityLabels: Record<string, string> = {
  wifi: "Free WiFi",
  meals: "All Meals",
  pool: "Pool",
  cafe: "Café",
  gym: "Gym",
  parking: "Parking",
};

interface Property {
  id: string;
  name: string;
  location: {
    street_address?: string;
    locality?: string;
    city?: string;
  };
  price: string;
  originalPrice?: string; // Assuming originalPrice might be available
  rating: number;
  reviews: number;
  image?: string;
  amenities: string[];
  badge?: string;
  bedType?: string;
  capacity?: string;
  genderType?: string;
  foodAvailable?: boolean;
  is_verified: boolean;
}

interface PropertyCardProps {
  property: Property;
  toggleFavorite: (id: string) => void;
  isFavorite: boolean;
  index: number; // For animation delay
  keyword: string;
  male_guests: number;
  female_guests: number;
  checkin: string;
  checkout: string;
  accommodation_type: string;
}

const getBadgeColor = (badge: string) => {
  switch (badge) {
    case "Best Seller":
      return "bg-primary text-primary-foreground";
    case "Top Rated":
      return "bg-secondary text-secondary-foreground";
    case "New":
      return "bg-green-500 text-white";
    case "Budget Pick":
      return "bg-blue-500 text-white";
    case "Trending":
      return "bg-purple-500 text-white";
    case "Premium":
      return "bg-gradient-to-r from-amber-500 to-orange-500 text-white";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  toggleFavorite,
  isFavorite,
  index,
  keyword,
  male_guests,
  female_guests,
  checkin,
  checkout,
  accommodation_type,
}) => {
  const navigate = useNavigate();
  const params = new URLSearchParams();
  params.append('keyword', keyword);
  params.append('male_guests', male_guests.toString());
  params.append('female_guests', female_guests.toString());
  params.append('checkin', checkin);
  params.append('checkout', checkout);
  params.append('accommodation_type', accommodation_type);
  const queryString = params.toString();

  const targetPath = `/property/${property.id}?${queryString}`;
  const amenities = (property.amenities || []).filter((a) => !(property.foodAvailable && a === "meals"));
  const gender = property.genderType ? genderTypeConfig[property.genderType.toLowerCase()] : undefined;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="group"
    >
      <div
        className="bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-border/50 cursor-pointer"
        onClick={() => navigate(targetPath)}
      >
        {/* Image Container */}
        <div className="relative h-52 overflow-hidden">
          <img
            src={property.image ? getImageUrl(property.image) : DEFAULT_PROPERTY_IMAGE}
            alt={property.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={(e) => {
              (e.target as HTMLImageElement).src = DEFAULT_PROPERTY_IMAGE;
            }}
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

          {/* Badge */}
          {property.badge && (
            <Badge
              className={cn(`absolute top-3 left-3 shadow-md`, getBadgeColor(property.badge))}
            >
              {property.badge}
            </Badge>
          )}

          {/* Favorite Button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleFavorite(property.id);
            }}
            className="absolute top-3 right-3 p-2 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors shadow-md"
          >
            <Heart
              size={18}
              className={
                isFavorite
                  ? "fill-red-500 text-red-500"
                  : "text-gray-600"
              }
            />
          </button>

          {/* Price Tag */}
          <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-md">
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-bold text-primary">₹{property.price}</span>
              {property.originalPrice && (
                <span className="text-xs text-muted-foreground line-through">₹{property.originalPrice}</span>
              )}
              <span className="text-xs text-muted-foreground">/night</span>
            </div>
          </div>

          {/* Gender Tag */}
          {gender && (
            <div
              className={cn("absolute bottom-3 right-3 backdrop-blur-sm rounded-lg px-2 py-1 text-xs flex items-center gap-1 shadow-md", gender.className)}
              title={`${gender.label} property`}
            >
              <gender.icon size={12} />
              {gender.label}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Rating */}
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center gap-1 bg-secondary/20 text-secondary-foreground px-2 py-0.5 rounded-full">
              <Star size={12} className="fill-secondary text-secondary" />
              <span className="text-xs font-semibold">{property.rating}</span>
            </div>
            <span className="text-xs text-muted-foreground">({property.reviews || 0} reviews)</span>
          </div>

          {/* Name */}
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
              {property.name}
            </h3>
            {property.is_verified && (
              <span title="Verified property" className="flex-shrink-0 flex items-center gap-0.5 text-green-600 text-xs font-medium">
                <BadgeCheck size={16} className="fill-green-100" />
                Verified
              </span>
            )}
          </div>

          {/* Location */}
          <div className="flex items-center gap-1 text-muted-foreground mb-3">
            <MapPin size={14} />
            <span className="text-sm truncate">
              {property.location ? `${property.location.street_address || ''}, ${property.location.city || ''}, ${property.location.locality || ''}` : "Unknown location"}
            </span>
          </div>

          {/* Amenities */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {property.foodAvailable && (
              <div className="flex items-center gap-1 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">
                <UtensilsCrossed size={12} />
                <span>Food included</span>
              </div>
            )}
            {amenities.slice(0, 3).map((amenity) => {
              const Icon = amenityIcons[amenity];
              return Icon ? (
                <div
                  key={amenity}
                  className="flex items-center gap-1 text-muted-foreground text-xs bg-muted px-2 py-1 rounded-full"
                >
                  <Icon size={12} />
                  <span className="hidden sm:inline">{amenityLabels[amenity]}</span>
                </div>
              ) : null;
            })}
            {amenities.length > 3 && (
              <span className="text-xs text-muted-foreground">+{amenities.length - 3}</span>
            )}
          </div>

          {/* Book Button */}
          <Link to={targetPath} onClick={(e) => e.stopPropagation()}>
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full">
              View Details
            </Button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default PropertyCard;